import {type RefCallback, useCallback, useEffect, useState} from 'react'

const STICKY_OBSERVER_OPTIONS: IntersectionObserverInit = {
  threshold: [1],
  rootMargin: '-1px 0px 0px 0px',
}

/**
 * Detects whether a `position: sticky` element is currently stuck.
 *
 * **Self-managed mode** (no arguments): returns `isSticky` and a `sentinelRef`
 * callback ref. Attach `sentinelRef` to a sentinel element (e.g. a zero-height
 * div) directly above or at the sticky attachment point.
 *
 * **Externally-managed mode** (`element` argument): observes the provided
 * element directly. Useful when a parent component owns the DOM node and
 * passes it down so multiple children can independently observe the same
 * element without re-rendering the parent.
 *
 * Uses `rootMargin: '-1px'` with `threshold: 1` so that when the sentinel's
 * intersection ratio drops below 1, we know the sticky element has engaged.
 */
export function useStickyObserver(): {isSticky: boolean; sentinelRef: RefCallback<Element>}
export function useStickyObserver(element: Element | null): {isSticky: boolean}
export function useStickyObserver(element?: Element | null): {
  isSticky: boolean
  sentinelRef?: RefCallback<Element>
} {
  const managed = arguments.length === 0
  const [isSticky, setIsSticky] = useState(false)

  const observe = useCallback((target: Element) => {
    const observer = new IntersectionObserver(entries => {
      const entry = entries[entries.length - 1]
      if (entry) {
        setIsSticky(entry.intersectionRatio < 1)
      }
    }, STICKY_OBSERVER_OPTIONS)
    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  const sentinelRef = useCallback<RefCallback<Element>>(
    node => {
      if (!node) return
      return observe(node)
    },
    [observe],
  )

  useEffect(() => {
    if (managed || !element) return
    return observe(element)
  }, [element, managed, observe])

  if (managed) {
    return {isSticky, sentinelRef}
  }
  return {isSticky}
}
